// src/pages/lab/processing/components/StartSampleProtocolModal.tsx
import React, { useState, useEffect } from "react";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Button, Badge, Radio, Select, Input, Tabs, Collapse,  Space, Typography, Row, Col,  Divider, message } from "antd";
import { CloseOutlined, PlayCircleOutlined, ClockCircleOutlined, FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

// Shared components
import { DataTable, SectionCard, DetailDrawer } from "../../../../../components/shared";

// API
import { createProcessingGroup } from "../../../../../api/labiq/GroupProcessingApi";
import { fetchTeamMembers } from "../../../../../api/admin/teamApi";

// Types
import { Sample } from "../../../../../types/lab/SampleTypes";
import { Protocol } from "../../../../../types/lab/ProtocolTypes";
import { User } from "../../../../../types/admin/UserTypes";

// Hooks
import { useNotifications } from "../../../../../context/NotificationContext";
import { TeamMember } from "../../../../../types/admin/TeamTypes";
import { hasAnyRoleInGroup } from "../../../../../utils/userPrefs/validatePermissions";
import { requireLabiqUserId } from "../../../../../utils/auth/labiqUserId";
import type { LegacyUserId } from "../../../../../types/identity";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface StartSampleProtocolModalProps {
	isOpen: boolean;
	samples: Sample[];
	protocols: Protocol[];
	onClose: () => void;
	onProtocolStarted: () => void;
}

const StartSampleProtocolModal: React.FC<StartSampleProtocolModalProps> = ({ isOpen, samples, protocols, onClose, onProtocolStarted }) => {
	const user: User = getSessionUser();
	
	const [compatibleProtocols, setCompatibleProtocols] = useState<Protocol[]>([]);
	const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
	const [assignedTo, setAssignedTo] = useState<LegacyUserId | undefined>(undefined);
	const [notes, setNotes] = useState<string>("");
	const [loading, setLoading] = useState(true);
	const [starting, setStarting] = useState(false);
	const [activeTab, setActiveTab] = useState("1");
	const [availableTechs, setAvailableTechs] = useState<TeamMember[]>([]);

	const [groupName, setGroupName] = useState<string>("");
	const [groupDescription, setGroupDescription] = useState<string>("");

	const { showSuccess, showError, showWarning } = useNotifications();

	useEffect(() => {
		if (isOpen) {
			loadCompatibleProtocols();
			loadTechnicians();

			if (!groupName) {
				const timestamp = new Date().toISOString().slice(0, 10);
				const sampleIds = samples
					.slice(0, 3)
					.map((s) => s.sampleID)
					.join(", ");
				const suffix = samples.length > 3 ? ` +${samples.length - 3} more` : "";
				setGroupName(`Group ${timestamp} - ${sampleIds}${suffix}`);
			}
		}
	}, [isOpen, samples]);

	const loadCompatibleProtocols = async () => {
		setLoading(true);
		try {
			// Filter for FIRST protocols only
			const allCompatibleProtocols = protocols.filter((p) => p.is_active && p.is_first_protocol);
			setCompatibleProtocols(allCompatibleProtocols);
		} catch (error) {
			showError("Failed to load compatible protocols");
			setCompatibleProtocols(protocols.filter((p) => p.is_active && p.is_first_protocol));
		} finally {
			setLoading(false);
		}
	};

	const loadTechnicians = async () => {
		try {
			let { data } = await fetchTeamMembers(1, 100);
			let techOptions: TeamMember[] = [];
			for (const tech of data) {
				if (hasAnyRoleInGroup("LAB", tech)) {
					techOptions.push(tech);
				}
			}
			setAvailableTechs(techOptions);
		} catch (error) {
			showError("Failed to load technicians");
		}
	};

	// Upgrading to not start just create the group and leave in a pending state 
	// so we can add samples and remove samples to the group before starting
	const handleCreateProtocolGroup = async () => {
		if (!selectedProtocol) {
			message.warning("Please select a protocol to start");
			return;
		}

		if (!groupName.trim()) {
			showWarning("Please enter a group name");
			return;
		}

		setStarting(true);
		try {
			const group = await createProcessingGroup({
				group_name: groupName,
				description: groupDescription || undefined,
				protocol_id: selectedProtocol.id,
				sample_ids: samples.map((s) => s.sampleID),
				created_by: requireLabiqUserId(user),
			});

			showSuccess(`Group processing started for "${groupName}" with ${samples.length} samples`);

			onProtocolStarted();
		} catch (error) {
			showError("Failed to start protocol");
			console.error("Protocol start error:", error);
		} finally {
			setStarting(false);
		}
	};

	const analyzeProtocol = (protocol: Protocol) => {
		const steps = protocol.steps || [];

		const totalDuration = steps.reduce((sum, step) => sum + step.duration_estimate, 0);
		const totalMaterials = steps.reduce((sum, step) => sum + (step.materials?.length || 0), 0);
		const stepsWithTimeout = steps.filter((step) => step.timeout_minutes).length;
		const stepsWithConditions = steps.filter((step) => step.completion_conditions).length;
		const totalActions = steps.reduce((sum, step) => sum + (step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0), 0);

		return {
			totalDuration,
			totalMaterials,
			stepsWithTimeout,
			stepsWithConditions,
			totalActions,
		};
	};

	const protocolColumns: ColumnsType<Protocol> = [
		{
			title: "Select",
			key: "select",
			render: (_: any, record: Protocol) => <Radio checked={selectedProtocol?.id === record.id} onChange={() => setSelectedProtocol(record)} />,
		},
		{
			title: "Protocol",
			key: "protocol",
			render: (_: any, record: Protocol) => {
				const analysis = analyzeProtocol(record);

				return (
					<div>
						<div style={{ marginBottom: "8px" }}>
							<Text strong style={{ fontSize: "13px" }}>
								{record.name}
							</Text>
							<Text type="secondary" style={{ fontSize: "11px", marginLeft: "8px" }}>
								v{record.version}
							</Text>
						</div>

						<Space wrap size={4} style={{ marginBottom: "8px" }}>
							<Badge count={`${record.steps?.length || 0} steps`} style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />
							{analysis.totalMaterials > 0 && <Badge count={`${analysis.totalMaterials} materials`} style={{ backgroundColor: "var(--mg-warning)", fontSize: "10px" }} />}
							{analysis.totalActions > 0 && <Badge count={`${analysis.totalActions} auto-actions`} style={{ backgroundColor: "var(--mg-success)", fontSize: "10px" }} />}
							{analysis.stepsWithTimeout > 0 && <Badge count={`${analysis.stepsWithTimeout} timeouts`} style={{ backgroundColor: "var(--mg-danger)", fontSize: "10px" }} />}
						</Space>

						{record.description && (
							<Text type="secondary" style={{  display: "block" }}>
								{record.description.length > 150 ? `${record.description.slice(0, 150)}...` : record.description}
							</Text>
						)}
					</div>
				);
			},
		},
		{
			title: "Duration",
			key: "duration",
			render: (_: any, record: Protocol) => {
				const analysis = analyzeProtocol(record);
				const hours = Math.floor(analysis.totalDuration / 60);
				const minutes = analysis.totalDuration % 60;

				return <Text >{hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}</Text>;
			},
		},
		{
			title: "Test Types",
			key: "test_types",
			render: (_: any, record: Protocol) => {
				const testTypes = record.test_types || [];
				if (testTypes.length === 0) return <Text type="secondary">Any</Text>;

				return (
					<Space wrap size={4}>
						{testTypes.slice(0, 2).map((type, index) => (
							<Badge key={index} count={type} style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />
						))}
						{testTypes.length > 2 && <Badge count={`+${testTypes.length - 2}`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />}
					</Space>
				);
			},
		},
		{
			title: "Sample Types",
			key: "sample_types",
			render: (_: any, record: Protocol) => {
				const sampleTypes = record.sample_types || [];
				if (sampleTypes.length === 0) return <Text type="secondary">Any</Text>;

				return (
					<Space wrap size={4}>
						{sampleTypes.slice(0, 2).map((type, index) => (
							<Badge key={index} count={type} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
						))}
						{sampleTypes.length > 2 && <Badge count={`+${sampleTypes.length - 2}`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />}
					</Space>
				);
			},
		},
	];

	const renderSetup = () => {
		if (samples.length === 0) return null;

		const uniqueAnalyses = [...new Set(samples.flatMap((s) => s.sampleOA?.analysis_requested || []))];
		const priorityLevels = samples.map((s) => s.sampleOA?.priority || 2);
		const maxPriority = Math.max(...priorityLevels);

		// labiq assigns technicians by legacy integer id.
		const technicianOptions = availableTechs
			.filter((tech) => tech.legacy_user_id != null)
			.map((tech) => ({
				label: `${tech.first_name} ${tech.last_name}`,
				value: tech.legacy_user_id,
			}));

		return (
			<Row gutter={[24, 16]}>
				<Col xs={24} md={12}>
					<Text strong style={{ display: "block", marginBottom: "12px" }}>
						Sample Information
					</Text>
					<Space direction="vertical" size="small" style={{ width: "100%" }}>
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<Text type="secondary">Total Samples:</Text>
							<Text strong>{samples.length}</Text>
						</div>

						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<Text type="secondary">Priority:</Text>
							<Text strong>{maxPriority === 5 ? "Critical" : maxPriority === 4 ? "Urgent" : maxPriority === 3 ? "High" : maxPriority === 2 ? "Normal" : "Low"}</Text>
						</div>

						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<Text type="secondary">Requested Analysis:</Text>
							<span>
								{uniqueAnalyses.length > 0 ? (
									<Space wrap size={4}>
										{uniqueAnalyses.map((analysis, index) => (
											<Badge key={index} count={analysis} style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />
										))}
									</Space>
								) : (
									<Text type="secondary">None specified</Text>
								)}
							</span>
						</div>

						<div>
							<Text type="secondary" style={{ display: "block", marginBottom: "8px" }}>
								Sample IDs:
							</Text>
							<table style={{ fontSize: "11px", borderCollapse: "collapse", width: "100%" }}>
								<tbody>
									{samples.slice(0, 5).map((s, idx) => (
										<tr key={idx}>
											<td style={{ padding: "4px 8px", borderBottom: "1px solid var(--mg-border)" }}>{s.sampleID}</td>
										</tr>
									))}
									{samples.length > 5 && (
										<tr>
											<td style={{ padding: "4px 8px", borderBottom: "1px solid var(--mg-border)", fontStyle: "italic" }}>+{samples.length - 5} more</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</Space>
				</Col>

				<Col xs={24} md={12}>
					<Space direction="vertical" size="middle" style={{ width: "100%" }}>
						<div>
							<Text>
								Group Name *
							</Text>
							<Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Enter group name" size="small" />
						</div>

						<div>
							<Text >
								Group Description (Optional)
							</Text>
							<TextArea value={groupDescription} onChange={(e) => setGroupDescription(e.target.value)} placeholder="Add notes about this processing group" rows={2} size="small" />
						</div>

						<div>
							<Text >
								Assign to Technician
							</Text>
							<Select value={assignedTo} onChange={(value) => setAssignedTo(value)} placeholder="Select technician" style={{ width: "100%" }} size="small" allowClear>
								{technicianOptions.map((tech) => (
									<Option key={tech.value} value={tech.value}>
										{tech.label}
									</Option>
								))}
							</Select>
						</div>

						<div>
							<Text>
								Processing Notes (Optional)
							</Text>
							<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any special notes, priorities, or instructions" rows={3} size="small" />
						</div>
					</Space>
				</Col>
			</Row>
		);
	};

	const renderProtocolPreview = () => {
		if (!selectedProtocol) return null;

		const analysis = analyzeProtocol(selectedProtocol);
		const steps = selectedProtocol.steps || [];

		return (
			<SectionCard>
				<Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
					<Col span={12}>
						<div style={{ fontSize: "13px" }}>
							<Text strong>Workflow Overview:</Text>
							<Space direction="vertical" size="small" style={{ width: "100%", marginTop: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Total Duration:</Text>
									<Text strong>
										{Math.floor(analysis.totalDuration / 60)}h {analysis.totalDuration % 60}m
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Materials Needed:</Text>
									<Text strong>{analysis.totalMaterials}</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Automation Actions:</Text>
									<Text strong>{analysis.totalActions}</Text>
								</div>
							</Space>
						</div>
					</Col>
					<Col span={12}>
						<div style={{ fontSize: "13px" }}>
							<Text strong>Advanced Features:</Text>
							<Space direction="vertical" size="small" style={{ width: "100%", marginTop: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Steps with Conditions:</Text>
									<Text strong>{analysis.stepsWithConditions}</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Steps with Timeouts:</Text>
									<Text strong>{analysis.stepsWithTimeout}</Text>
								</div>
							</Space>
						</div>
					</Col>
				</Row>

				<Collapse
					ghost
					items={[
						{
							key: "1",
							label: (
								<Space>
									<FileTextOutlined />
									<span>Step-by-Step Workflow ({steps.length} steps)</span>
								</Space>
							),
							children: (
								<Space direction="vertical" size="small" style={{ width: "100%" }}>
									{steps.map((step) => (
										<SectionCard key={step.id} style={{ border: "1px solid var(--mg-border)" }}>
											<div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
												<Badge count={step.step_number} style={{ backgroundColor: "var(--mg-primary)" }} />
												<div style={{ flex: 1 }}>
													<Space style={{ marginBottom: "4px" }}>
														<Text strong style={{ fontSize: "13px" }}>
															{step.name}
														</Text>
														<Badge count={step.step_type} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
														<Badge count={`${Math.floor(step.duration_estimate / 60)}h ${step.duration_estimate % 60}m`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
													</Space>

													{step.description && (
														<Text type="secondary" style={{  display: "block", marginBottom: "8px" }}>
															{step.description.length > 100 ? `${step.description.slice(0, 100)}...` : step.description}
														</Text>
													)}

													<Space wrap size={4}>
														{(step.method?.length || 0) > 0 && <Badge count={`${step.method!.length} methods`} style={{ backgroundColor: "var(--mg-success)", fontSize: "10px" }} />}
														{(step.materials?.length || 0) > 0 && <Badge count={`${step.materials!.length} materials`} style={{ backgroundColor: "var(--mg-warning)", fontSize: "10px" }} />}
														{step.completion_conditions && <Badge count="conditions" style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />}
														{(step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0) > 0 && (
															<Badge
																count={`${(step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0)} actions`}
																style={{ backgroundColor: "var(--mg-info)", fontSize: "10px" }}
															/>
														)}
														{step.timeout_minutes && <Badge count={`${step.timeout_minutes}min timeout`} style={{ backgroundColor: "var(--mg-danger)", fontSize: "10px" }} />}
													</Space>
												</div>
											</div>
										</SectionCard>
									))}
								</Space>
							),
						},
					]}
				/>
			</SectionCard>
		);
	};

	return (
		<DetailDrawer
			title={`Start Group Protocol: ${samples.length} Samples`}
			open={isOpen}
			onClose={onClose}
			headerActions={
				<Space>
					<Button icon={<CloseOutlined />} onClick={onClose} disabled={starting} size="small">
						Cancel
					</Button>
					<Button type="primary" icon={<PlayCircleOutlined />} onClick={handleCreateProtocolGroup} disabled={!selectedProtocol || starting || !groupName.trim()} loading={starting} size="small">
						Start Group Processing
					</Button>
				</Space>
			}
		>
			{renderSetup()}

			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				items={[
					{
						key: "1",
						label: "Available Protocols",
						children:
							loading ? (
								<div style={{ textAlign: "center", padding: "32px" }}>
									<Text type="secondary">Loading compatible protocols...</Text>
								</div>
							) : compatibleProtocols.length > 0 ? (
								<div style={{ maxHeight: "400px", overflow: "auto" }}>
									<DataTable
										bare
										columns={protocolColumns}
										dataSource={compatibleProtocols}
										rowKey="id"
										pagination={false}
										onRowClick={(record) => setSelectedProtocol(record)}
									/>
								</div>
							) : (
								<div style={{ textAlign: "center", padding: "32px" }}>
									<ExclamationCircleOutlined style={{ fontSize: "48px", color: "var(--mg-border)", marginBottom: "16px" }} />
									<Title level={5} type="secondary">
										No Compatible Protocols Found
									</Title>
									<Text type="secondary" >
										No active protocols match the selected samples' requirements.
									</Text>
								</div>
							),
					},
					{
						key: "2",
						label: "Protocol Preview",
						disabled: !selectedProtocol,
						children: renderProtocolPreview(),
					},
				]}
			/>
		</DetailDrawer>
	);
};

export default StartSampleProtocolModal;
